import Config from "./Config";

import avatarURL from "../../../../../../../../../utils/avatarURL";
import { truncateText } from "../utils";

import { Label } from "../UseChartOptions/useChartOptions";

const bellCurveImage = new Image();
bellCurveImage.src = "/static/img/charts/BellCurve.png";

const getBarChartPlugins = (xLabelText, avatarCache) => {
	return {
		id: "y-axis-labels",
		afterDatasetsDraw: (chart) => {
			const ctx = chart.ctx;
			const yAxis = chart.scales.y;
			const labels = chart.data.labels as Label[];

			// draw avatars for y
			yAxis.ticks.forEach((tick, index) => {
				const currentLabelData = labels[index] as Label;
				const avatar =
					currentLabelData && currentLabelData.type === "employee"
						? avatarCache[avatarURL(currentLabelData._id as string)]
						: null;
				const imageWidth = 20;
				const imageHeight = 20;
				// Calculate the y position for the image based on the tick
				const y = yAxis.getPixelForTick(index) + yAxis.paddingTop - 10 - imageHeight / 2;
				const x = yAxis.left - 125;

				// Coordinates for the center of the image area
				const centerX = x + imageWidth / 2;
				const centerY = y + imageWidth / 2;

				// Save the current state of the canvas
				ctx.save();

				// Start drawing a path for the circular clip
				ctx.beginPath();
				// Create a circular path centered around the image area
				ctx.arc(centerX, centerY, imageWidth / 2, 0, Math.PI * 2, true);
				// Clip to the circle
				ctx.clip();

				// Draw the avartar within the clipping path (circle) if avatar exist
				if (avatar) {
					ctx.drawImage(avatar, x, y, imageWidth, imageWidth);
				}

				// Reset the path for drawing the border
				ctx.beginPath();
				ctx.arc(centerX, centerY, imageWidth / 2, 0, Math.PI * 2, true);
				ctx.strokeStyle = "white"; // Set border color
				ctx.lineWidth = 1; // Set border width
				ctx.stroke(); // Draw the border
				ctx.restore();

				// Draw text next to the image
				ctx.fillStyle = "black";
				ctx.font = "14px Source Sans Pro";

				const truncatedText = labels[index]
					? truncateText(ctx, labels[index].name, 100 + (avatar ? 0 : imageWidth))
					: "";
				const textXPostion = x + 5 + (avatar ? imageWidth : 0);
				const textYPosition = centerY + 5;
				ctx.fillText(truncatedText, textXPostion, textYPosition);
			});

			// draw label for x
			const xAxis = chart.scales.x;
			ctx.save();
			ctx.textAlign = "center";
			ctx.fillStyle = "black";
			const truncatedText = truncateText(ctx, xLabelText, xAxis.width * 0.7);
			ctx.fillText(truncatedText, (xAxis.left + xAxis.right) / 2, chart.height - 85);
			ctx.restore();
		},
	};
};

const getColumnChartPlugins = (yLabelText) => ({
	id: "x-axis-labels",
	afterDatasetsDraw: (chart) => {
		const ctx = chart.ctx;
		const xAxis = chart.scales.x;
		const labels = chart.data.labels as Label[];
		const tickSpacing = xAxis.width / xAxis.ticks.length;

		const inferredRange = Config.CALCULATION_FUNCTIONS.inferRange(xAxis.ticks.length);
		// rotate the text depending on the number of ticks and the width
		const rotateLabels =
			tickSpacing < Config.TICK_SPACING_ROTATION_BREAKPOINT &&
			xAxis.width <= Config.RANGE_SETTINGS[inferredRange].MAX_ROTATABLE_TABLE_WIDTH;
		const rotationAmount = rotateLabels ? Config.CALCULATION_FUNCTIONS.rotationAmount(tickSpacing) : 0;
		const rotatedSpace = Config.CALCULATION_FUNCTIONS.rotatedSpace(rotationAmount);

		xAxis.ticks.forEach((_tick, index: number) => {
			const tickX = xAxis.getPixelForTick(index);

			const text = rotateLabels
				? truncateText(ctx, labels[index].name, rotatedSpace)
				: truncateText(ctx, labels[index].name, tickSpacing - 10);

			ctx.fillStyle = "black";

			ctx.font = "14px Source Sans Pro";
			ctx.textAlign = rotateLabels ? "right" : "center";
			ctx.textBaseline = !rotateLabels ? "middle" : rotationAmount > -1 ? "top" : "middle";
			const textY = chart.chartArea.bottom + (rotateLabels ? 8 : 20);
			ctx.save();
			// rotate the text from textX, textY as pivot point
			ctx.translate(tickX, textY);

			if (rotateLabels) {
				ctx.rotate(rotationAmount);
			}

			// Draw the text
			ctx.fillText(text, 0, 0);
			ctx.restore();
		});

		// draw label for y
		const yAxis = chart.scales.y;
		ctx.save();
		ctx.textAlign = "center";
		ctx.fillStyle = "black";
		ctx.font = "14px Source Sans Pro";
		ctx.translate(yAxis.left - 22, (yAxis.top + yAxis.bottom) / 2);
		ctx.rotate(-Math.PI / 2);
		const truncatedText = truncateText(ctx, yLabelText, yAxis.height * 0.7);
		ctx.fillText(truncatedText, 0, 0);
		ctx.restore();
	},
});

const drawTopBorderPlugin = {
	id: "drawTopBorder",
	afterDatasetsDraw: (chart) => {
		const {
			ctx,
			chartArea: { top, left, right },
		} = chart;
		ctx.save();
		ctx.beginPath();
		ctx.moveTo(left, top);
		ctx.lineTo(right, top);
		ctx.strokeStyle = "#e9eaec";
		ctx.lineWidth = 1;
		ctx.stroke();
		ctx.restore();
	},
};

const drawBellCurvePlugin = {
	id: "drawBellCurve",
	afterDatasetsDraw: (chart) => {
		const {
			ctx,
			chartArea: { top, left, width, height },
		} = chart;
		ctx.save();
		ctx.drawImage(bellCurveImage, left, top + 20, width, height - 20);
		ctx.restore();
	},
};

export { getBarChartPlugins, getColumnChartPlugins, drawTopBorderPlugin, drawBellCurvePlugin };
